import React from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  usePrograms,
  useProgramOperations,
  useProgramSearch,
} from "../../../hooks/usePrograms";
import { useSearchableList } from "../../../hooks/useSearchableList";
import { useCrudModals } from "../../../hooks/useCrudModals";
import {
  TrainingProgram,
  CreateProgramInput,
  UpdateProgramInput,
  ProgramFilters,
} from "../../../types/program";
import ProgramsTable from "../../../components/programs/ProgramsTable";
import ProgramModal from "../../../components/programs/ProgramModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import Pagination from "../../../components/common/Pagination";
import ListPageShell from "../../../components/common/ListPageShell";

const LIMIT = 30;

const Programs: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const modals = useCrudModals<TrainingProgram>();

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialSearchTerm = searchParams.get("search") || "";
  const initialChallengeId = searchParams.get("challengeId")
    ? parseInt(searchParams.get("challengeId")!, 10)
    : undefined;

  const initialFilters: ProgramFilters = {};
  if (initialSearchTerm) initialFilters.search = initialSearchTerm;
  if (initialChallengeId) initialFilters.challengeId = initialChallengeId;

  const { programs, filters, updateFilters, limit, ...programList } =
    usePrograms(initialPage, LIMIT, initialFilters);
  const programSearch = useProgramSearch();

  const list = useSearchableList<TrainingProgram, number>({
    list: { items: programs, ...programList },
    search: programSearch,
    initialSearchTerm,
    initialPage,
    searchExtra: filters.challengeId,
    urlParams: { challengeId: filters.challengeId },
  });

  const {
    loading: operationLoading,
    createProgram,
    updateProgram,
    deleteProgram,
    error: operationError,
  } = useProgramOperations();

  const handleViewProgram = (program: TrainingProgram) => {
    navigate(`/admin/programs/${program.id}`, {
      state: { from: location.search },
    });
  };

  const handleChallengeChange = (newChallengeId: number | undefined) => {
    updateFilters({ ...filters, challengeId: newChallengeId });

    if (list.hasSearched && list.lastSearchTerm) {
      programSearch.search(list.lastSearchTerm, 1, false, newChallengeId);
    }
  };

  const confirmDeleteProgram = async () => {
    if (!modals.toDelete) return;

    const success = await deleteProgram(modals.toDelete.id);
    if (success) {
      list.refreshAll();
      modals.closeDelete();
    }
  };

  const handleCreateSubmit = async (programData: any) => {
    const createData: CreateProgramInput = {
      name: programData.name,
      program_type: programData.program_type,
      difficulty_level: programData.difficulty_level,
      duration_weeks: programData.duration_weeks,
      training_days_per_week: programData.training_days_per_week,
      workout_duration: programData.workout_duration,
      description: programData.description,
      is_visible: programData.is_visible,
    };

    const newProgram = await createProgram(createData);
    if (newProgram) {
      list.refreshAll();
      modals.closeCreate();
    }
  };

  const handleUpdateSubmit = async (programData: any) => {
    if (!modals.selected) return;

    const updateData: UpdateProgramInput = {
      name: programData.name,
      program_type: programData.program_type,
      difficulty_level: programData.difficulty_level,
      description: programData.description,
      is_visible: programData.is_visible,
    };

    const updatedProgram = await updateProgram(modals.selected.id, updateData);
    if (updatedProgram) {
      list.refreshAll();
      modals.closeEdit();
    }
  };

  return (
    <>
      <ListPageShell
        entityName="programs"
        onRefresh={list.handleRefresh}
        refreshDisabled={list.listLoading}
        onAdd={modals.openCreate}
        addLabel="Add Program"
        error={list.error}
        errorContext={list.hasSearched ? "searching" : "loading"}
        operationError={operationError}
      >
        <ProgramsTable
          programs={list.items}
          loading={list.loading || operationLoading}
          onEdit={modals.openEdit}
          onDelete={modals.openDelete}
          onView={handleViewProgram}
          onSearch={list.handleSearch}
          onClearSearch={list.handleClearSearch}
          searchTerm={list.lastSearchTerm}
          isSearchMode={list.hasSearched}
          challengeId={filters.challengeId}
          onChallengeChange={handleChallengeChange}
        />

        <Pagination
          page={list.page}
          limit={limit}
          total={list.total}
          totalPages={list.totalPages}
          entityName="programs"
          isSearchMode={list.hasSearched}
          onPageChange={list.onPageChange}
          loading={list.loading}
        />
      </ListPageShell>

      <ProgramModal
        isOpen={modals.showCreate}
        onClose={modals.closeCreate}
        onSubmit={handleCreateSubmit}
        mode="create"
        loading={operationLoading}
        error={operationError}
      />

      <ProgramModal
        isOpen={modals.showEdit}
        onClose={modals.closeEdit}
        onSubmit={handleUpdateSubmit}
        program={modals.selected}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={modals.showDelete}
        onClose={modals.closeDelete}
        onConfirm={confirmDeleteProgram}
        title="Delete Program"
        message="Are you sure you want to delete this training program?"
        itemName={modals.toDelete?.name || "Untitled Program"}
        loading={operationLoading}
        error={operationError}
      />
    </>
  );
};

export default Programs;
